<template>
  <div class="gam-gambit-card">
    <RouterLink :to="proposalLink" class="gambit-link" />

    <div class="title-timeframe">
      <div class="timeframe">
        <span class="date-time">
          <GamGambitDayTag
            v-if="isToday(new Date(proposal.start)) || isTomorrow(new Date(proposal.start))"
            :date="proposal.start"
            class="date"
          />

          <span v-else class="date">
            {{ GambitHelper.formatTime(proposal.start, { omitTime: true }).join(' ') }}
          </span>

          &middot;

          <span class="time start-time">
            {{ GambitHelper.formatTime(proposal.start, { omitDay: true }).join(' ') }}
          </span>
        </span>
      </div>
    </div>

    <div v-if="proposal.location" class="location">
      <span class="name">{{ proposal.location.name }}</span>
    </div>

    <div class="gambit-tags-wrapper">
      <div class="proposal-chips">
        <GamChip v-bind="invitationChip" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { SegmentProposal } from '@/core/data/spark/spark.interface';
import { localize } from '@/core/gambits';
import GambitHelper from '@/core/helpers/gambit.helper';
import GamGambitDayTag from '@/views/components/gam-gambits/GamGambitDayTag.vue';
import GamChip from '@/views/components/GamChip.vue';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import { isToday, isTomorrow } from 'date-fns';
import { computed } from 'vue';

const props = defineProps<{
  proposal: SegmentProposal;
  date: string;
}>();

const proposalLink = computed(() => ({
  name: RouterNameType.SPARKS,
  query: {
    date: props.date.substring(0, 10),
    action: 'results',
    view: `proposal:${props.proposal.id}`,
    from: 'card',
    back: 'history',
  },
}));

const invitationChip = computed(
  (): GamChipType => ({
    ...GambitHelper.gambitTag(localize('player.sections.respond.badge.invitation'), {
      icon: GamIconName.PAWN,
      variant: GamChipVariant.PRIMARY,
      size: GamChipSize.X_SMALL,
    }),
    isActive: true,
  }),
);
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-gambit-card {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-md);
  position: relative;
  width: 100%;
  border: 1px solid transparent;
  padding: 16px;
  text-decoration: none;

  @extend %tinted-card;

  @include hover {
    background-color: var(--color-dark-500);
    box-shadow: var(--shadow-card-hover);
  }

  .gambit-link {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
  }

  .title-timeframe {
    display: flex;
    flex-direction: column;
    gap: 9px;
    width: 100%;
  }

  .title {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    position: relative;
    overflow: hidden;
    width: 100%;
    margin-top: -1px;
    margin-bottom: 1px;
    letter-spacing: 0.02em;
    font-weight: 700;

    span {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      text-overflow: ellipsis;
      word-break: break-word;
      hyphens: auto;
      overflow: hidden;
      background: var(--color-linear-gradient);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      padding-right: 2em;
    }
  }

  .timeframe {
    display: flex;
    gap: 0.3em;
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: pre;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-white-60);

    .date-time {
      display: flex;
      align-items: center;
      gap: 0.05em;
      color: var(--color-white-60);
    }

    .date {
      color: var(--color-white);
    }

    :deep(.day-tag.now),
    :deep(.day-tag.soon),
    :deep(.day-tag.today),
    :deep(.day-tag.tomorrow) {
      -webkit-background-clip: unset !important;
      background-clip: unset !important;
      -webkit-text-fill-color: var(--color-pink) !important;
      background: none !important;
      color: var(--color-pink) !important;
    }

    .time {
      color: var(--color-white-60);
    }

    .start-time {
      color: var(--color-white);
    }
  }

  .location {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.33rem;
    width: 100%;
    font-size: 14px;
    letter-spacing: 0.02em;

    .name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex-shrink: 0;
      max-width: 100%;
      color: var(--color-white-60);
    }
  }

  .gambit-tags-wrapper {
    margin-top: 4px;
    margin-left: -16px;
    margin-right: -16px;
    width: calc(100% + 32px);
  }

  .proposal-chips {
    display: flex;
    gap: 0.25rem;
    padding: 0 16px;
  }
}
</style>
